import { useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AuthPageShell, SideRailShell } from '@/components';
import { useOrgAdminOf } from '@/auth.ts';
import { useAuthFetch } from '@/hooks';
import { ROUTES } from '@/routes.ts';
import { EuroCircleIcon, PaletteIcon, TableIcon } from '@/components/icons.tsx';
import type { OrgAdminOrganizationListResponse } from '@dermalink/shared';
import { usePracticeAdmin, type PracticeAdminContext } from './practiceAdminContext.ts';
import styles from './PracticeAdmin.module.css';

export function PracticeAdmin() {
  const orgAdminOf = useOrgAdminOf();
  const authFetch  = useAuthFetch();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery<OrgAdminOrganizationListResponse>({
    queryKey: ['org-admin-organizations'],
    queryFn:  async () => {
      const res = await authFetch('/org-admin/organizations');
      if (!res.ok) throw new Error('Failed to load practices');
      return res.json();
    },
    enabled: orgAdminOf.length > 0,
  });

  if (orgAdminOf.length === 0) {
    return (
      <AuthPageShell>
        <p className="text-muted status-message">U heeft geen toegang tot dit scherm. Enkel praktijkbeheerders hebben toegang.</p>
      </AuthPageShell>
    );
  }

  const organizations = data?.organizations ?? [];
  const org = organizations.find((o) => o.id === selectedId) ?? organizations[0];

  return (
    <SideRailShell
      label="Praktijkbeheer navigatie"
      items={[
        { to: ROUTES.practiceAdminReport,   label: 'Overzicht', icon: <TableIcon size={20} /> },
        { to: ROUTES.practiceAdminBranding, label: 'Huisstijl', icon: <PaletteIcon size={20} /> },
        { to: ROUTES.practiceAdminPayments, label: 'Betalingen', icon: <EuroCircleIcon size={20} /> },
      ]}
    >
      {isLoading && <p className="text-muted status-message">Laden…</p>}
      {isError && <p className="text-muted status-message">Kon praktijkgegevens niet laden.</p>}
      {org && <Outlet context={{ organizations, org, selectOrg: setSelectedId } satisfies PracticeAdminContext} />}
    </SideRailShell>
  );
}

/** Page title block shared by the practice admin pages, with a practice switcher for admins of several. */
export function PracticeAdminHead({ title, children }: { title: string; children?: React.ReactNode }) {
  const { organizations, org, selectOrg } = usePracticeAdmin();
  return (
    <div className={styles.pageHead}>
      <div className={styles.titleGroup}>
        {organizations.length > 1 ? (
          <select className={styles.practiceSelect} value={org.id} onChange={(e) => selectOrg(Number(e.target.value))} aria-label="Praktijk">
            {organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        ) : (
          <span className={styles.kicker}>{org.name}</span>
        )}
        <h1 className={styles.heading}>{title}</h1>
      </div>
      {children && <div className={styles.headActions}>{children}</div>}
    </div>
  );
}
